import React from "react";
import { Select } from "subframe/components/Select";
import { TabsPills } from "subframe/components/TabsPills";
import * as SubframeCore from "@subframe/core";
import { ExitSurveyFilter } from "modules/Retention/types/exitSurvey";
import { ExitSurveyStats } from "./ExitSurveyStats";
import { fetchOfficeLocations } from "services/retention/employees";
import { useQuery } from "@tanstack/react-query";
import endpoints from "constants/retention/endpoints";
import { getJobsForAgency } from "services/applications";


interface ExitSurveyFiltersProps {
  filters: ExitSurveyFilter;
  status: string;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  onFilterChange: (filters: ExitSurveyFilter) => void;
  onFilterReset?: (filters: ExitSurveyFilter) => void;
  showTimeFilter?: boolean;
  showAllStats?: boolean;
}

export const ExitSurveyFilters: React.FC<ExitSurveyFiltersProps> = ({ 
  filters, 
  status,
  setStatus,
  onFilterChange,
  onFilterReset,
  showTimeFilter = true,
  showAllStats = true,
}) => {

  const { data: officeLocations } = useQuery({
    queryKey: [endpoints.officeLocations],
    queryFn: fetchOfficeLocations,
    select: (res) => res.result.office_locations.sort() || [],
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  }); 

  const { data: jobs } = useQuery({
    queryKey: [endpoints.jobs],
    queryFn: getJobsForAgency,
    select: (res: any) => res.result.jobs
  });

  const handleTimeRangeChange = (value: string) => {
    onFilterChange({ ...filters, timeRange: value });
  };

  const handleBranchChange = (value: string) => {
    onFilterChange({ ...filters, branch: value });
  };

  const handleJobChange = (value: string) => {
    onFilterChange({ ...filters, job: value });
  };

  const handleRankByChange = (value: string) => {
    onFilterChange({ ...filters, rankBy: value });
  };

  return (
    <>
      {showTimeFilter && (
        <Select
          label=""
          placeholder="All time"
          helpText=""
          icon="FeatherCalendar"
          value={filters.timeRange}
          onValueChange={handleTimeRangeChange}
        >
          <Select.Item value="all">All time</Select.Item>
          <Select.Item value="1">Last 24 hours</Select.Item>
          <Select.Item value="7">Last 7 days</Select.Item>
          <Select.Item value="14">Past 2 weeks</Select.Item>
          <Select.Item value="30">Last 30 days</Select.Item>
          <Select.Item value="90">Last 3 months</Select.Item>
          <Select.Item value="ytd">Year to Date</Select.Item>
          <Select.Item value="365">Last 12 months</Select.Item>
        </Select>
      )}

      <ExitSurveyStats 
        showAllStats={showAllStats} 
        status={status}
        setStatus={setStatus}
        filters={filters}
        onFilterChange={onFilterReset || onFilterChange}
      />

      <div className="flex w-full flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body font-body text-default-font">Show</span>
          <SubframeCore.Icon
            className="text-body font-body text-default-font"
            name="FeatherListFilter"
          />
        </div>
        <Select
          className="h-auto w-auto flex-none self-stretch"
          label=""
          placeholder="All branch"
          helpText=""
          value={filters.branch}
          onValueChange={handleBranchChange}
        >
          <Select.Item value="all">All locations</Select.Item>
          {officeLocations?.map((location: any) => (
            <Select.Item key={location} value={location}>{location}</Select.Item>
          ))}
        </Select>
        <Select
          className="h-auto w-auto flex-none self-stretch"
          label=""
          placeholder="All jobs"
          helpText=""
          value={filters.job || "all"}
          onValueChange={handleJobChange}
        >
          <Select.Item value="all">All jobs</Select.Item>
          {jobs?.map((job: any) => (
            <Select.Item key={job.id} value={job.id}>{job.job_title}</Select.Item>
          ))}
        </Select>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body font-body text-default-font">Rank by</span>
          <SubframeCore.Icon
            className="text-body font-body text-default-font"
            name="FeatherArrowUpDown"
          />
          <Select
            className="h-auto w-auto flex-none self-stretch"
            label=""
            placeholder="Exit date"
            helpText=""
            value={filters.rankBy}
            onValueChange={handleRankByChange}
          >
            <Select.Item value="exit_date">Exit date</Select.Item>
            <Select.Item value="status">Status</Select.Item>
          </Select>
        </div>
        <TabsPills className="h-auto w-auto flex-none" />
      </div>
    </>
  );
}; 